import { createContext, useContext } from 'react';
import type { CurrentUser, LoginInput, LoginResponse } from '@brimostech/shared';

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

export interface AuthContextValue {
  status: AuthStatus;
  user: CurrentUser | null;
  /** Resolves with `mfa` when a second factor is required (no session yet). */
  login: (input: LoginInput) => Promise<LoginResponse>;
  verifyMfa: (input: {
    mfaToken: string;
    code?: string;
    recoveryCode?: string;
  }) => Promise<CurrentUser>;
  /** Accepts an authenticated response (invite acceptance) as the current session. */
  adoptSession: (response: LoginResponse) => void;
  /** Replaces the access token (after MFA confirmation) and reloads the profile. */
  replaceAccessToken: (accessToken: string) => Promise<void>;
  refreshUser: () => Promise<CurrentUser | null>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
  switchCondominium: (condominiumId: string) => Promise<CurrentUser>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

/** The authenticated user. Only valid under RequireAuth. */
export function useCurrentUser(): CurrentUser {
  const { user } = useAuth();
  if (!user) throw new Error('useCurrentUser requires an authenticated user');
  return user;
}
